import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { 
  ArrowLeft, 
  Plus, 
  Search,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  ChefHat,
  Clock,
  Flame,
  GripVertical,
  X,
  Image as ImageIcon,
  Upload,
  Filter
} from "lucide-react";
import { cn } from "../../lib/utils";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OBJETIVOS = [
  { id: "definición", label: "Definición", color: "text-blue-400", bg: "bg-blue-500/10" },
  { id: "musculación", label: "Musculación", color: "text-red-400", bg: "bg-red-500/10" },
  { id: "recomposición", label: "Recomposición", color: "text-purple-400", bg: "bg-purple-500/10" },
];

// CATEGORÍAS PRINCIPALES (una sola por receta)
const CATEGORIAS_PRINCIPALES = [
  { id: "desayuno", label: "Desayuno", color: "text-yellow-400", bg: "bg-yellow-500/10" },
  { id: "almuerzo_merienda", label: "Almuerzo/Merienda", color: "text-orange-400", bg: "bg-orange-500/10" },
  { id: "comida", label: "Comida", color: "text-green-400", bg: "bg-green-500/10" },
  { id: "cena", label: "Cena", color: "text-blue-400", bg: "bg-blue-500/10" },
  { id: "postres", label: "Postres", color: "text-pink-400", bg: "bg-pink-500/10" },
  { id: "snacks", label: "Snacks", color: "text-cyan-400", bg: "bg-cyan-500/10" },
  { id: "sin_categoria", label: "Sin categoría", color: "text-[#666]", bg: "bg-[#2A2A2A]" },
];

// TAGS SECUNDARIOS (múltiples opcionales)
const TAGS = [
  { id: "alto_proteina", label: "Alto en proteína" },
  { id: "bajo_carbs", label: "Bajo en carbos" },
  { id: "rapido", label: "Rápido" },
  { id: "batch_cooking", label: "Batch Cooking" },
  { id: "para_llevar", label: "Para llevar" },
  { id: "post_entreno", label: "Post-entreno" },
  { id: "vegano", label: "Vegano" },
  { id: "sin_gluten", label: "Sin gluten" },
  { id: "dulce", label: "Dulce" },
  { id: "salado", label: "Salado" },
];

const DIFICULTADES = [
  { id: "muy fácil", label: "Muy fácil" },
  { id: "fácil", label: "Fácil" },
  { id: "media", label: "Media" },
  { id: "difícil", label: "Difícil" },
];

// Empty recipe template
const EMPTY_RECIPE = {
  nombre: "",
  descripcion: "",
  objetivo: "recomposición",
  category: "sin_categoria",
  categorias: [],
  tiempo_minutos: 15,
  dificultad: "fácil",
  ingredientes: [],
  preparacion: [],
  macros: { calorias: 0, proteina: 0, carbos: 0, grasas: 0 },
  tips: "",
  image: null,
  active: true
};

// ============================================
// IMAGE UPLOADER COMPONENT
// ============================================
const ImageUploader = ({ value, onChange }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Solo se permiten imágenes");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("La imagen debe ser menor a 5MB");
      return;
    }

    setUploading(true);

    try {
      // Compress and convert to base64
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();

      img.onload = () => {
        // Max dimensions
        const maxWidth = 800;
        const maxHeight = 600;
        let { width, height } = img;

        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        const base64 = canvas.toDataURL("image/jpeg", 0.7);
        onChange(base64);
        setUploading(false);
      };

      img.onerror = () => {
        toast.error("Error al procesar imagen");
        setUploading(false);
      };

      img.src = URL.createObjectURL(file);
    } catch (error) {
      toast.error("Error al subir imagen");
      setUploading(false);
    }
  };

  const handleRemove = () => {
    onChange(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className="space-y-2">
      <label className="text-[#888] text-xs block">Imagen de la receta</label>
      
      {value ? (
        <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-[#1A1A1A] border border-[#2A2A2A]">
          <img src={value} alt="Preview" className="w-full h-full object-cover" />
          <button
            type="button"
            onClick={handleRemove}
            className="absolute top-2 right-2 p-1.5 bg-red-500/80 hover:bg-red-500 rounded-full text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className={cn(
            "w-full aspect-video rounded-lg border-2 border-dashed border-[#2A2A2A] bg-[#1A1A1A]",
            "flex flex-col items-center justify-center gap-2 transition-all",
            "hover:border-[#D4AF37]/50 hover:bg-[#1A1A1A]/80",
            uploading && "opacity-50 cursor-wait"
          )}
        >
          {uploading ? (
            <div className="w-6 h-6 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <Upload className="w-8 h-8 text-[#666]" />
              <span className="text-[#666] text-sm">Subir imagen</span>
              <span className="text-[#555] text-xs">JPG, PNG (max 5MB)</span>
            </>
          )}
        </button>
      )}
      
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
};

// ============================================
// INGREDIENT EDITOR COMPONENT
// ============================================
const IngredientEditor = ({ ingredientes, onChange }) => {
  const addIngredient = () => {
    onChange([...ingredientes, { nombre: "", cantidad: "", nota: "" }]);
  };

  const updateIngredient = (index, field, value) => {
    const updated = [...ingredientes];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const removeIngredient = (index) => {
    onChange(ingredientes.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-[#888] text-xs">Ingredientes</label>
        <button
          type="button"
          onClick={addIngredient}
          className="text-[#D4AF37] text-xs hover:underline"
        >
          + Añadir
        </button>
      </div>
      
      {ingredientes.length === 0 ? (
        <p className="text-[#555] text-xs py-4 text-center">Sin ingredientes</p>
      ) : (
        <div className="space-y-2">
          {ingredientes.map((ing, idx) => (
            <div key={idx} className="flex gap-2 items-start">
              <GripVertical className="w-4 h-4 text-[#444] mt-2.5 flex-shrink-0" />
              <Input
                value={ing.cantidad}
                onChange={(e) => updateIngredient(idx, "cantidad", e.target.value)}
                placeholder="150g"
                className="w-20 bg-[#0B0B0B] border-[#2A2A2A] text-white text-sm"
              />
              <Input
                value={ing.nombre}
                onChange={(e) => updateIngredient(idx, "nombre", e.target.value)}
                placeholder="Pechuga de pollo"
                className="flex-1 bg-[#0B0B0B] border-[#2A2A2A] text-white text-sm"
              />
              <button
                type="button"
                onClick={() => removeIngredient(idx)}
                className="p-2 text-red-400 hover:bg-red-400/10 rounded"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================
// STEP EDITOR COMPONENT
// ============================================
const StepEditor = ({ pasos, onChange }) => {
  const addStep = () => {
    onChange([...pasos, ""]);
  };

  const updateStep = (index, value) => {
    const updated = [...pasos];
    updated[index] = value;
    onChange(updated);
  };

  const removeStep = (index) => {
    onChange(pasos.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-[#888] text-xs">Pasos de preparación</label>
        <button
          type="button"
          onClick={addStep}
          className="text-[#D4AF37] text-xs hover:underline"
        >
          + Añadir paso
        </button>
      </div>
      
      {pasos.length === 0 ? (
        <p className="text-[#555] text-xs py-4 text-center">Sin pasos</p>
      ) : (
        <div className="space-y-2">
          {pasos.map((paso, idx) => (
            <div key={idx} className="flex gap-2 items-start">
              <span className="w-6 h-6 rounded-full bg-[#D4AF37]/20 text-[#D4AF37] text-xs flex items-center justify-center flex-shrink-0 mt-1">
                {idx + 1}
              </span>
              <Textarea
                value={paso}
                onChange={(e) => updateStep(idx, e.target.value)}
                placeholder={`Paso ${idx + 1}...`}
                className="flex-1 bg-[#0B0B0B] border-[#2A2A2A] text-white text-sm min-h-[60px]"
              />
              <button
                type="button"
                onClick={() => removeStep(idx)}
                className="p-2 text-red-400 hover:bg-red-400/10 rounded"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================
// RECIPE EDITOR COMPONENT
// ============================================
const RecipeEditor = ({ recipe, onSave, onCancel, saving }) => {
  const [formData, setFormData] = useState(recipe || EMPTY_RECIPE);

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleTag = (tagId) => {
    const current = formData.categorias || [];
    if (current.includes(tagId)) {
      updateField("categorias", current.filter(c => c !== tagId));
    } else {
      updateField("categorias", [...current, tagId]);
    }
  };

  const handleSubmit = () => {
    if (!formData.nombre?.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }
    onSave(formData);
  };

  return (
    <div className="space-y-6 max-h-[80vh] overflow-y-auto px-1">
      {/* Image Upload */}
      <ImageUploader 
        value={formData.image} 
        onChange={(img) => updateField("image", img)} 
      />

      {/* Nombre */}
      <div>
        <label className="text-[#888] text-xs block mb-1">Nombre *</label>
        <Input
          value={formData.nombre}
          onChange={(e) => updateField("nombre", e.target.value)}
          placeholder="Ej: Bowl de arroz con pollo"
          className="bg-[#1A1A1A] border-[#2A2A2A] text-white"
        />
      </div>

      {/* Descripción */}
      <div>
        <label className="text-[#888] text-xs block mb-1">Descripción</label>
        <Textarea
          value={formData.descripcion}
          onChange={(e) => updateField("descripcion", e.target.value)}
          placeholder="Descripción breve de la receta..."
          className="bg-[#1A1A1A] border-[#2A2A2A] text-white min-h-[80px]"
        />
      </div>

      {/* Categoría Principal */}
      <div>
        <label className="text-[#888] text-xs block mb-1">Categoría Principal *</label>
        <Select value={formData.category || "sin_categoria"} onValueChange={(v) => updateField("category", v)}>
          <SelectTrigger className="bg-[#1A1A1A] border-[#2A2A2A] text-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A]">
            {CATEGORIAS_PRINCIPALES.map(cat => (
              <SelectItem key={cat.id} value={cat.id} className="text-white hover:bg-[#2A2A2A]">
                {cat.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Objetivo y Dificultad */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-[#888] text-xs block mb-1">Objetivo</label>
          <Select value={formData.objetivo} onValueChange={(v) => updateField("objetivo", v)}>
            <SelectTrigger className="bg-[#1A1A1A] border-[#2A2A2A] text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A]">
              {OBJETIVOS.map(obj => (
                <SelectItem key={obj.id} value={obj.id} className="text-white hover:bg-[#2A2A2A]">
                  {obj.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[#888] text-xs block mb-1">Dificultad</label>
          <Select value={formData.dificultad} onValueChange={(v) => updateField("dificultad", v)}>
            <SelectTrigger className="bg-[#1A1A1A] border-[#2A2A2A] text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A]">
              {DIFICULTADES.map(dif => (
                <SelectItem key={dif.id} value={dif.id} className="text-white hover:bg-[#2A2A2A]">
                  {dif.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Tiempo */}
      <div>
        <label className="text-[#888] text-xs block mb-1">Tiempo (minutos)</label>
        <Input
          type="number"
          value={formData.tiempo_minutos}
          onChange={(e) => updateField("tiempo_minutos", parseInt(e.target.value) || 0)}
          className="bg-[#1A1A1A] border-[#2A2A2A] text-white w-32"
        />
      </div>

      {/* Tags Secundarios */}
      <div>
        <label className="text-[#888] text-xs block mb-2">Tags (opcionales)</label>
        <div className="flex flex-wrap gap-2">
          {TAGS.map(tag => (
            <button
              key={tag.id}
              type="button"
              onClick={() => toggleTag(tag.id)}
              className={cn(
                "px-3 py-1.5 rounded-full text-xs font-medium transition-all",
                formData.categorias?.includes(tag.id)
                  ? "bg-[#D4AF37] text-[#0B0B0B]"
                  : "bg-[#1A1A1A] text-[#888] hover:bg-[#2A2A2A]"
              )}
            >
              {tag.label}
            </button>
          ))}
        </div>
      </div>

      {/* Macros */}
      <div>
        <label className="text-[#888] text-xs block mb-2">Macros por porción</label>
        <div className="grid grid-cols-4 gap-2">
          <div>
            <span className="text-[#555] text-[10px] block mb-1">Calorías</span>
            <Input
              type="text"
              inputMode="decimal"
              value={formData.macros?.calorias_input ?? formData.macros?.calorias ?? ''}
              onChange={(e) => {
                // Permitir solo números, punto y coma
                const raw = e.target.value.replace(/[^0-9.,]/g, '');
                // Mantener como string para permitir escribir "64," mientras se completa
                updateField("macros", { 
                  ...formData.macros, 
                  calorias_input: raw,
                  calorias: raw === '' ? 0 : parseFloat(raw.replace(',', '.')) || 0 
                });
              }}
              onBlur={(e) => {
                // Al perder foco, normalizar el valor
                const val = e.target.value.replace(',', '.');
                const num = parseFloat(val) || 0;
                updateField("macros", { 
                  ...formData.macros, 
                  calorias_input: num.toString(),
                  calorias: num 
                });
              }}
              className="bg-[#0B0B0B] border-[#2A2A2A] text-white text-sm"
              placeholder="0"
            />
          </div>
          <div>
            <span className="text-[#555] text-[10px] block mb-1">Proteína (g)</span>
            <Input
              type="text"
              inputMode="decimal"
              value={formData.macros?.proteina_input ?? formData.macros?.proteina ?? ''}
              onChange={(e) => {
                const raw = e.target.value.replace(/[^0-9.,]/g, '');
                updateField("macros", { 
                  ...formData.macros, 
                  proteina_input: raw,
                  proteina: raw === '' ? 0 : parseFloat(raw.replace(',', '.')) || 0 
                });
              }}
              onBlur={(e) => {
                const val = e.target.value.replace(',', '.');
                const num = parseFloat(val) || 0;
                updateField("macros", { 
                  ...formData.macros, 
                  proteina_input: num.toString(),
                  proteina: num 
                });
              }}
              className="bg-[#0B0B0B] border-[#2A2A2A] text-white text-sm"
              placeholder="0"
            />
          </div>
          <div>
            <span className="text-[#555] text-[10px] block mb-1">Carbos (g)</span>
            <Input
              type="text"
              inputMode="decimal"
              value={formData.macros?.carbos_input ?? formData.macros?.carbos ?? ''}
              onChange={(e) => {
                const raw = e.target.value.replace(/[^0-9.,]/g, '');
                updateField("macros", { 
                  ...formData.macros, 
                  carbos_input: raw,
                  carbos: raw === '' ? 0 : parseFloat(raw.replace(',', '.')) || 0 
                });
              }}
              onBlur={(e) => {
                const val = e.target.value.replace(',', '.');
                const num = parseFloat(val) || 0;
                updateField("macros", { 
                  ...formData.macros, 
                  carbos_input: num.toString(),
                  carbos: num 
                });
              }}
              className="bg-[#0B0B0B] border-[#2A2A2A] text-white text-sm"
              placeholder="0"
            />
          </div>
          <div>
            <span className="text-[#555] text-[10px] block mb-1">Grasas (g)</span>
            <Input
              type="text"
              inputMode="decimal"
              value={formData.macros?.grasas_input ?? formData.macros?.grasas ?? ''}
              onChange={(e) => {
                const raw = e.target.value.replace(/[^0-9.,]/g, '');
                updateField("macros", { 
                  ...formData.macros, 
                  grasas_input: raw,
                  grasas: raw === '' ? 0 : parseFloat(raw.replace(',', '.')) || 0 
                });
              }}
              onBlur={(e) => {
                const val = e.target.value.replace(',', '.');
                const num = parseFloat(val) || 0;
                updateField("macros", { 
                  ...formData.macros, 
                  grasas_input: num.toString(),
                  grasas: num 
                });
              }}
              className="bg-[#0B0B0B] border-[#2A2A2A] text-white text-sm"
              placeholder="0"
            />
          </div>
        </div>
      </div>

      {/* Ingredientes */}
      <IngredientEditor 
        ingredientes={formData.ingredientes || []} 
        onChange={(v) => updateField("ingredientes", v)} 
      />

      {/* Pasos */}
      <StepEditor 
        pasos={formData.preparacion || []} 
        onChange={(v) => updateField("preparacion", v)} 
      />

      {/* Tips */}
      <div>
        <label className="text-[#888] text-xs block mb-1">Tips / Notas</label>
        <Textarea
          value={formData.tips}
          onChange={(e) => updateField("tips", e.target.value)}
          placeholder="Consejos, variaciones, etc..."
          className="bg-[#1A1A1A] border-[#2A2A2A] text-white min-h-[60px]"
        />
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-4 border-t border-[#2A2A2A]">
        <Button variant="outline" onClick={onCancel} className="flex-1 border-[#2A2A2A] text-white">
          Cancelar
        </Button>
        <Button 
          onClick={handleSubmit} 
          disabled={saving}
          className="flex-1 bg-[#D4AF37] hover:bg-[#B8962E] text-[#0B0B0B]"
        >
          {saving ? "Guardando..." : (recipe ? "Actualizar" : "Crear")}
        </Button>
      </div>
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================
const AdminRecipes = () => {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [showEditor, setShowEditor] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetchRecipes = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/recipes`, { headers: getAuthHeaders() });
      setRecipes(response.data.recipes || []);
    } catch (error) {
      toast.error("Error al cargar recetas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecipes();
  }, []);

  const handleCreate = () => {
    setEditingRecipe(null);
    setShowEditor(true);
  };

  const handleEdit = (recipe) => {
    setEditingRecipe(recipe);
    setShowEditor(true);
  };

  const handleSave = async (formData) => {
    setSaving(true);
    try {
      if (editingRecipe) {
        await axios.put(
          `${API_URL}/api/admin/recipes/${editingRecipe.id}`,
          formData,
          { headers: getAuthHeaders() }
        );
        toast.success("Receta actualizada");
      } else {
        await axios.post(
          `${API_URL}/api/admin/recipes`,
          formData,
          { headers: getAuthHeaders() }
        );
        toast.success("Receta creada");
      }
      setShowEditor(false);
      fetchRecipes();
    } catch (error) {
      toast.error("Error al guardar receta");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (recipe) => {
    try {
      await axios.patch(
        `${API_URL}/api/admin/recipes/${recipe.id}/toggle`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(recipe.active ? "Receta desactivada" : "Receta activada");
      fetchRecipes();
    } catch (error) {
      toast.error("Error al cambiar estado");
    }
  };

  const handleDelete = async (recipe) => {
    try {
      await axios.delete(
        `${API_URL}/api/admin/recipes/${recipe.id}`,
        { headers: getAuthHeaders() }
      );
      toast.success("Receta eliminada");
      setDeleteConfirm(null);
      fetchRecipes();
    } catch (error) {
      toast.error("Error al eliminar receta");
    }
  };

  // Filter recipes
  const filteredRecipes = useMemo(() => {
    return recipes.filter(r => {
      // Search filter
      const matchesSearch = 
        r.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.objetivo?.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Category filter
      const recipeCategory = r.category || "sin_categoria";
      const matchesCategory = categoryFilter === "all" || recipeCategory === categoryFilter;
      
      return matchesSearch && matchesCategory;
    });
  }, [recipes, searchTerm, categoryFilter]);

  // Category counts
  const categoryCounts = useMemo(() => {
    const counts = { all: recipes.length };
    CATEGORIAS_PRINCIPALES.forEach(cat => {
      counts[cat.id] = recipes.filter(r => (r.category || "sin_categoria") === cat.id).length;
    });
    return counts;
  }, [recipes]);

  const activeCount = recipes.filter(r => r.active !== false).length;
  const inactiveCount = recipes.filter(r => r.active === false).length;

  return (
    <div className="min-h-screen bg-[#0B0B0B] pb-24" data-testid="admin-recipes-page">
      {/* Header */}
      <div className="sticky top-0 bg-[#0B0B0B]/95 backdrop-blur-sm border-b border-[#2A2A2A] p-4 z-10">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => navigate("/admin")} className="p-2 -ml-2 text-white hover:bg-white/10 rounded-lg">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-white font-bold text-lg">Recetas Premium</h1>
            <p className="text-[#666] text-xs">
              {recipes.length} recetas ({activeCount} activas, {inactiveCount} ocultas)
            </p>
          </div>
          <Button
            onClick={handleCreate}
            className="bg-[#D4AF37] hover:bg-[#B8962E] text-[#0B0B0B]"
          >
            <Plus className="w-4 h-4 mr-2" /> Nueva
          </Button>
        </div>

        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar recetas..."
            className="pl-10 bg-[#1A1A1A] border-[#2A2A2A] text-white"
          />
        </div>

        {/* Category Filters */}
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
          <button
            onClick={() => setCategoryFilter("all")}
            className={cn(
              "px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all",
              categoryFilter === "all"
                ? "bg-[#D4AF37] text-[#0B0B0B]"
                : "bg-[#1A1A1A] text-[#888] hover:bg-[#2A2A2A]"
            )}
          >
            Todas ({categoryCounts.all})
          </button>
          {CATEGORIAS_PRINCIPALES.map(cat => (
            categoryCounts[cat.id] > 0 && (
              <button
                key={cat.id}
                onClick={() => setCategoryFilter(cat.id)}
                className={cn(
                  "px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all",
                  categoryFilter === cat.id
                    ? "bg-[#D4AF37] text-[#0B0B0B]"
                    : "bg-[#1A1A1A] text-[#888] hover:bg-[#2A2A2A]"
                )}
              >
                {cat.label} ({categoryCounts[cat.id]})
              </button>
            )
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredRecipes.length === 0 ? (
          <div className="text-center py-20">
            <ChefHat className="w-12 h-12 text-[#444] mx-auto mb-4" />
            <p className="text-[#666]">No hay recetas</p>
            <Button onClick={handleCreate} className="mt-4 bg-[#D4AF37] text-[#0B0B0B]">
              Crear primera receta
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredRecipes.map(recipe => {
              const objetivo = OBJETIVOS.find(o => o.id === recipe.objetivo);
              const categoria = CATEGORIAS_PRINCIPALES.find(c => c.id === (recipe.category || "sin_categoria"));
              
              return (
                <div 
                  key={recipe.id}
                  className={cn(
                    "bg-[#1A1A1A] rounded-xl overflow-hidden border transition-all",
                    recipe.active !== false 
                      ? "border-[#2A2A2A]" 
                      : "border-[#2A2A2A] opacity-60"
                  )}
                  data-testid={`admin-recipe-${recipe.id}`}
                >
                  <div className="flex">
                    {/* Image Thumbnail */}
                    <div className="w-24 h-24 sm:w-32 sm:h-32 flex-shrink-0 bg-[#0B0B0B]">
                      {recipe.image ? (
                        <img src={recipe.image} alt={recipe.nombre} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ImageIcon className="w-8 h-8 text-[#333]" />
                        </div>
                      )}
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1 p-3 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h3 className="text-white font-semibold text-sm truncate">{recipe.nombre}</h3>
                        {recipe.active === false && (
                          <span className="px-2 py-0.5 bg-[#2A2A2A] text-[#666] text-[10px] rounded flex-shrink-0">
                            Oculta
                          </span>
                        )}
                      </div>
                      
                      {/* Category & Objective badges */}
                      <div className="flex flex-wrap gap-1 mb-2">
                        {categoria && (
                          <span className={cn("px-2 py-0.5 rounded text-[10px] font-medium", categoria.bg, categoria.color)}>
                            {categoria.label}
                          </span>
                        )}
                        {objetivo && (
                          <span className={cn("px-2 py-0.5 rounded text-[10px]", objetivo.bg, objetivo.color)}>
                            {objetivo.label}
                          </span>
                        )}
                      </div>
                      
                      {/* Meta info */}
                      <div className="flex items-center gap-3 text-[#666] text-xs">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {recipe.tiempo_minutos}min
                        </span>
                        <span className="flex items-center gap-1">
                          <Flame className="w-3 h-3" /> {recipe.macros?.calorias || 0} kcal
                        </span>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex gap-1 mt-2">
                        <button
                          onClick={() => handleEdit(recipe)}
                          className="p-1.5 text-[#888] hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleToggle(recipe)}
                          className="p-1.5 text-[#888] hover:text-white hover:bg-white/10 rounded"
                        >
                          {recipe.active !== false ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(recipe)}
                          className="p-1.5 text-[#888] hover:text-red-400 hover:bg-red-400/10 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Recipe Editor Dialog */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="bg-[#121212] border-[#2A2A2A] text-white max-w-lg max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="text-[#D4AF37]">
              {editingRecipe ? "Editar Receta" : "Nueva Receta"}
            </DialogTitle>
          </DialogHeader>
          <RecipeEditor
            recipe={editingRecipe}
            onSave={handleSave}
            onCancel={() => setShowEditor(false)}
            saving={saving}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="bg-[#121212] border-[#2A2A2A] text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-red-400">Eliminar Receta</DialogTitle>
          </DialogHeader>
          <p className="text-[#888] text-sm">
            ¿Estás seguro de que quieres eliminar <span className="text-white">{deleteConfirm?.nombre}</span>?
          </p>
          <div className="flex gap-3 mt-4">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)} className="flex-1 border-[#2A2A2A] text-white">
              Cancelar
            </Button>
            <Button onClick={() => handleDelete(deleteConfirm)} className="flex-1 bg-red-500 hover:bg-red-600 text-white">
              Eliminar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminRecipes;
